/**
 * Waitlist Stats Hook — Beta-only admin analytics.
 *
 * DELETE THIS FILE when beta mode ends. All waitlist UI sections
 * are gated by isBetaMode() so removing this file + the conditional
 * renders is a clean operation with no side effects.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { rpc } from '@/lib/supabase/rpc';
import { isBetaMode, mode } from '@/lib/config/app-mode';

export interface WaitlistStats {
  // Counts
  total: number;
  verified: number;
  waiting: number;
  invited: number;
  converted: number;
  // Role split
  startups: number;
  investors: number;
  undecided: number;
  // Referrals
  totalReferrals: number;
  avgReferrals: number;
  topReferrers: { email: string; referral_count: number }[];
  // Trends
  signupsToday: number;
  signupsThisWeek: number;
  dailyTrend: { date: string; count: number }[];
  // Conversion
  inviteRate: number; // invited / total %
  conversionRate: number; // converted / invited %
}

// ── Mock data ──

const MOCK_STATS: WaitlistStats = {
  total: 247,
  verified: 231,
  waiting: 198,
  invited: 38,
  converted: 11,
  startups: 142,
  investors: 78,
  undecided: 27,
  totalReferrals: 89,
  avgReferrals: 0.36,
  topReferrers: [
    { email: 'sarah@techstart.io', referral_count: 12 },
    { email: 'marc@alpinevc.ch', referral_count: 8 },
    { email: 'lea@fintech.co', referral_count: 6 },
  ],
  signupsToday: 5,
  signupsThisWeek: 23,
  dailyTrend: [
    { date: '03/23', count: 4 },
    { date: '03/24', count: 6 },
    { date: '03/25', count: 3 },
    { date: '03/26', count: 5 },
    { date: '03/27', count: 2 },
    { date: '03/28', count: 3 },
    { date: '03/29', count: 5 },
  ],
  inviteRate: 15.4,
  conversionRate: 28.9,
};

// ── Hook ──

export function useWaitlistStats() {
  return useQuery<WaitlistStats | null>({
    queryKey: ['admin-waitlist-stats'],
    queryFn: async () => {
      // Only available during beta
      if (!isBetaMode()) return null;

      if (mode.showSampleData) {
        return MOCK_STATS;
      }

      // Server-side aggregation via RPC (replaces unbounded client-side fetch)
      const { data, error } = await rpc('get_waitlist_stats');

      if (error) throw error;
      if (!data) {
        return {
          total: 0, verified: 0, waiting: 0, invited: 0, converted: 0,
          startups: 0, investors: 0, undecided: 0,
          totalReferrals: 0, avgReferrals: 0, topReferrers: [],
          signupsToday: 0, signupsThisWeek: 0, dailyTrend: [],
          inviteRate: 0, conversionRate: 0,
        };
      }

      return data as unknown as WaitlistStats;
    },
    enabled: isBetaMode(),
    staleTime: 2 * 60 * 1000,
  });
}
